import React, { useContext, useEffect, useRef, useState } from 'react';
import ABeagleContext from '../../contexts/abeagle';
import BuzzContext from '../../contexts/buzz';
import CommentsContext from '../../contexts/comments';
import {
  trackClientExternalLink,
  trackClientImpression,
  trackClientContentAction,
} from '../../hooks/analytics/client-event-tracking';
import { getReferrer } from '@buzzfeed/bf-utils/lib/referrer';

import { AppPromoButton, AppPromoCard, AppPromoQuizPartyCard } from './components';
import { PromoData, resolvePromoVariantContent } from './promoConfig'
import {
  CARD_STYLE_EXPERIMENT,
  isCardStyleSuppressedVariant,
} from '../../utils/cardStyleQuizExperiment';
import { isGamesPost } from '../../utils/games';

import styles from './mobileAppPromo.module.scss'


const TAG_YOURSELF_EXPERIMENT = 'BF-16768-Tag-Yourself-No-Callout-Unit';
const AD_INJECTION_EXPERIMENT = 'RT-2202-ad-injection-card-style-quiz';

// don't ever show app promos unless this is true
const GlobalBuzzCheck = buzz => (
  !buzz.isShopping &&
  buzz.language == 'en' &&
  buzz.destination_name === 'buzzfeed' &&
  !buzz.isTimedTrivia &&
  ['Moderated Community', 'Editorial'].includes(buzz.editorial_status)
);

// when the user closes the promo we'll save the date in localStorage
//   and hide the promo for this many days
const HIDE_DAYS = 7;
const STORAGE_KEY = 'MobileAppPromo_closed_at';


function MobileAppPromo() {
  const { buzz = {} } = useContext(BuzzContext);
  const { isCommentsPanelOpen } = useContext(CommentsContext);
  const { experiments } = useContext(ABeagleContext);
  const [device, setDevice] = useState(undefined);
  const [isVisible, setIsVisible] = useState(undefined);
  const [promoContent, setPromoContent] = useState(undefined)
  const [isQuizComplete, setIsQuizComplete] = useState(false);
  const latestPromoContext = useRef({ buzz, experiments, params: null });
  const adInjectionVariant = experiments?.eligible?.[AD_INJECTION_EXPERIMENT]?.value;
  const isAdInjectionActive = ['v1', 'v2', 'v3'].includes(adInjectionVariant);
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const params = new URLSearchParams(search);

  latestPromoContext.current = { buzz, experiments, params };

  // spoof branch apis that are used by quizzes
  // eventually we can rework this but it will require changes to quiz bfp code
  const branchPolyfill = {
    addListener: () => { return true },
    removeListener: () => { return true },
    track: () => { return true },
    logEvent: () => { return true },
    closeJourney: () => {
      setIsQuizComplete(true);
      const { buzz: currentBuzz, experiments: currentExperiments, params: currentParams } = latestPromoContext.current;
      const completionPromo = PromoData.find(p =>
        p.enabled &&
        p.name.endsWith('-complete') &&
        p.match(currentBuzz, undefined, { experiments: currentExperiments, params: currentParams })
      );
      setPromoContent(resolvePromoVariantContent({
        promo: completionPromo,
        buzz: currentBuzz,
        experiments: currentExperiments,
        params: currentParams,
      }) || {});
      return true;
    },
  }

  // CET params used for all tracking calls
  const trackingParams = {
    unit_type: 'modal',
    subunit_type: 'component',
    position_in_unit: 0,
    position_in_subunit: 0,
    item_type: 'button',
    item_name: 'cta',
  }

  // get browser info and hydrate state
  useEffect(() => {
    try {
      if (device === undefined) {
        window.branch = branchPolyfill;
        const userAgent = window.navigator.userAgent;
        const _device =
          /iP(hone|ad|od)/i.test(userAgent) ? 'ios' :
          /Android/i.test(userAgent) ? 'android' :
          'desktop';
        setDevice(_device);
      }

      if (isVisible === undefined) {
        const closedDate = localStorage.getItem(STORAGE_KEY);
        const now = new Date().getTime();
        const hideMilliseconds = HIDE_DAYS * (24 * 60 * 60 * 1000);
        const isClosed = closedDate ? now - closedDate < hideMilliseconds : false;
        setIsVisible(!isClosed);
      }
    }
    catch (err) {
      console.error(err);
      return err;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // ABeagle loads after mount. Do not cache the non-experiment fallback first.
    if (buzz.isQuiz && experiments && !experiments.loaded && !params.has('abdebug')) {
      return;
    }

    const referrer = getReferrer();
    const promo = PromoData.find(entry =>
      entry.match(buzz, referrer, { experiments, params }) &&
      (isQuizComplete ? entry.name.endsWith('-complete') : !entry.name.endsWith('-complete'))
    );
    setPromoContent(promo?.enabled ? resolvePromoVariantContent({
      promo,
      buzz,
      experiments,
      params,
    }) : false);
  }, [buzz, experiments, isQuizComplete, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // fire an impression when the promo updates (including first view)
  useEffect(() => {
    if (promoContent && promoContent?.promoType && isVisible) {
      const storeLink = promoContent.storeLinks[device] + promoContent.campaignId;
      // https://github.com/buzzfeed/mono/blob/master/data_registry_api/datatypes/client_tracking_events/impressions/web_impressions_bf.yml
      trackClientImpression(buzz, {
        target_content_id: storeLink,
        target_content_type: 'url',
        unit_name: promoContent?.unit_name || promoContent.campaignId,
        subunit_name: promoContent?.subunit_name || 'mobile_app_promo',
        ...trackingParams,
      });
    }
  }, [promoContent]);

  // --- BF-16768 Tag Yourself experiment: DELETE this block when the experiment graduates ---
  // Hides the promo for users in the Tag Yourself experiment or when the new quiz design
  // is forced via ?tag_yourself. The new design needs more screen space on mobile.
  //
  // When graduating:
  //   1. Delete everything between the START/END markers (including the useContext import above)
  //   2. Remove the TAG_YOURSELF_EXPERIMENT constant and the ABeagleContext import
  //   3. Confirm with PM which quiz types get the new design permanently
  //   4. Add to GlobalBuzzCheck: !buzz.isTrivia && !buzz.<confirmedType>
  const hasTagYourselfParam = params?.has('tag_yourself');
  const isAbDebugOverride = params?.has('abdebug')
    && params?.has(`abeagle_${TAG_YOURSELF_EXPERIMENT}`);
  const hasCardStyleAbDebugOverride = params?.has('abdebug')
    && params?.has(`abeagle_${CARD_STYLE_EXPERIMENT}`);
  const cardStyleDebugVariant = params?.get(`abeagle_${CARD_STYLE_EXPERIMENT}`);
  const isCardStyleAbDebugOverride = hasCardStyleAbDebugOverride
    && isCardStyleSuppressedVariant(cardStyleDebugVariant);
  if (
    buzz.isQuiz &&
    !experiments?.loaded &&
    !hasTagYourselfParam &&
    !isAbDebugOverride &&
    !hasCardStyleAbDebugOverride
  ) {
    return null;
  }
  const isTagYourselfActive = hasTagYourselfParam
    || isAbDebugOverride
    || experiments?.eligible?.[TAG_YOURSELF_EXPERIMENT]?.value != null;
  const isCardStyleActive = isCardStyleSuppressedVariant(
    experiments?.eligible?.[CARD_STYLE_EXPERIMENT]?.value
  ) || isCardStyleAbDebugOverride;
  // --- BF-16768 Tag Yourself experiment: END ---

  const isValidBuzz = GlobalBuzzCheck(buzz);
  if (
    !isValidBuzz ||
    !isVisible ||
    !promoContent ||
    isCommentsPanelOpen ||
    isTagYourselfActive ||
    isCardStyleActive ||
    isAdInjectionActive ||
    device === 'desktop' ||
    isGamesPost(buzz)
  ) {
    return null;
  }

  const handleClick = () => {
    const storeLink = promoContent.storeLinks[device] + promoContent.campaignId;
    // https://github.com/buzzfeed/mono/blob/master/data_registry_api/datatypes/client_tracking_events/external_links/web_external_links_bf.yml
    trackClientExternalLink(buzz, {
      target_content_url: storeLink,
      unit_name: promoContent?.unit_name || promoContent.campaignId,
      subunit_name: promoContent?.subunit_name || 'mobile_app_promo',
      ...trackingParams,
    });
    window.location = storeLink;
  };

  const handleClose = () => {
    // https://github.com/buzzfeed/mono/blob/master/data_registry_api/datatypes/client_tracking_events/content_actions/web_content_actions_bf.yml
    trackClientContentAction(buzz, {
      action_type: 'close',
      action_value: 'close_promo',
      unit_name: promoContent?.unit_name || promoContent.campaignId,
      subunit_name: promoContent?.subunit_name || 'mobile_app_promo',
      ...trackingParams,
    });
    let now = new Date().getTime();
    localStorage.setItem(STORAGE_KEY, now);
    setIsVisible(false);
  }

  const PromoComponent = (props) => {
    switch (props.content.promoType) {
      case 'button':
        return <AppPromoButton {...props} />
      case 'card':
        return <AppPromoCard {...props} />
      case 'quiz-party-card':
        return <AppPromoQuizPartyCard {...props} />
      default:
        return null;
    }
  }

  return (
    <section id='mobile-app-promo' className={`${styles.mobileAppPromo} ${promoContent.promoType === 'button' ? styles.promoButton : styles.promoBanner}`}>
      <PromoComponent content={promoContent} onClick={handleClick} onClose={handleClose} />
    </section>
  );
}

export default MobileAppPromo;
